import type { Observable } from "rxjs";
import {
  type AnyKeyHasher,
  type KeyHasher,
  type KeyHasherKeyType,
  type KeyHasherPatternType,
  ArrayKeyHasher,
  StringKeyHasher,
} from "@repo/lib/keyHasher";
import {
  type AnyClock,
  type Clock,
  type ClockDurationType,
  type ClockTimeType,
  PerformanceClock,
} from "@repo/lib/clock";
import { ExpirationCache } from "@repo/shared/expirationCache";
import { ObservableCache } from "./observableCache";

type Key<H extends AnyKeyHasher> = KeyHasherKeyType<H>;
type Pattern<H extends AnyKeyHasher> = KeyHasherPatternType<H>;
type Time<C extends AnyClock> = ClockTimeType<C>;
type Duration<C extends AnyClock> = ClockDurationType<C>;

export type FetchHandler<
  K,
  V,
  H extends KeyHasher<Key<H>, Pattern<H>>,
  C extends Clock<Time<C>, Duration<C>>,
> = (options: {
  /** The RepoCache instance. */
  repoCache: RepoCache<K, V, H, C>;
  /** The RepoCache's publish method. Shortcut for `repoCache.publish`. */
  publish: (entryKey: K, entryValue: V | null) => void;
  /** The request key to run a fetch for. */
  requestKey: Key<H>;
}) => void | Promise<void>;

export type CollectHandler<
  K,
  V,
  H extends KeyHasher<Key<H>, Pattern<H>>,
  C extends Clock<Time<C>, Duration<C>>,
> = (options: {
  /** The RepoCache instance. */
  repoCache: RepoCache<K, V, H, C>;
  /** The request key to collect the data for. */
  requestKey: Key<H>;
  /** The map containing the data. */
  table: ReadonlyMap<K, V>;
}) => unknown;

export type AffectedKeysHandler<
  K,
  V,
  H extends KeyHasher<Key<H>, Pattern<H>>,
  C extends Clock<Time<C>, Duration<C>>,
> = (options: {
  /** The RepoCache instance. */
  repoCache: RepoCache<K, V, H, C>;
  /** The id / key of the entry that just changed. */
  entryKey: K;
  /** The new value of the entry object. This will always contain available data, even if this
   * data is being deleted. This is critical when the request keys are derived from parts of the
   * entry. */
  entryData: V;
  /** Indicates that the change occurred because the entry was deleted. */
  deleted: boolean;
  /** Call this function for all request keys that are affected by the update.
   *
   * If a pattern can be used to match affected keys, prefer using `affectsMatchingKeys` over
   * use of `repoCache.getMatchingKeys` combined with `affectsKey`. */
  affectsKey: (requestKey: Key<H>) => void;
  /** Call this function whenever all keys matching a given pattern are affected.
   *
   * Prefer this over `repoCache.getMatchingKeys` which is less efficient, since `affectedKeys`
   * is called many times when lists of data are being processed. `affectsMatchingKeys` queues
   * unique patterns to be matched later, rather than running match tests immediately. */
  affectsMatchingKeys: (pattern: Pattern<H>) => void;
}) => void;

export type FirstLastSubscribeHandler<
  K,
  V,
  H extends KeyHasher<Key<H>, Pattern<H>>,
  C extends Clock<Time<C>, Duration<C>>,
> = (options: {
  /** The RepoCache instance. */
  repoCache: RepoCache<K, V, H, C>;
  /** The request key that was just initially subscribed to or fully unsubscribed from. */
  requestKey: Key<H>;
  /** The request key, hashed as a string. */
  strKey: string;
}) => void;

export type SubscriptionPublishedHandler<
  K,
  V,
  H extends KeyHasher<Key<H>, Pattern<H>>,
  C extends Clock<Time<C>, Duration<C>>,
> = (options: {
  /** The RepoCache instance. */
  repoCache: RepoCache<K, V, H, C>;
  /** The request key that data is about to be published for. */
  requestKey: Key<H>;
  /** The request key, hashed as a string. */
  strKey: string;
  /** The data collected for this key. */
  collectedData: unknown;
}) => void;

export interface GetObservableOptions {
  /** When set to true, forces the data to reload, even if it's cached. Defaults to false.
   * Not applicable when `initialFetch` is false. */
  forceReload?: boolean;
  /** When set to true, submits an initial synchronous (if cached) or asynchronous (if
   * fetched) call to 'next' on the subscriber, supplying the requested data. When false,
   * only changes and `publish` calls will trigger 'next' on the subscription. */
  initialFetch?: boolean;
}

export interface RepoCacheOptions<
  /** The type for the data key. */
  K,
  /** The type for the data value. */
  V,
  /** The type of key hasher used for hashing request keys. */
  H extends KeyHasher<Key<H>, Pattern<H>>,
  /** The type of clock used for handling cache timings. */
  C extends Clock<Time<C>, Duration<C>>,
> {
  /** A method that will be called when data needs to be fetched from the server. Once data is
   * fetched, it should call `repoCache.publish` for each data entry downloaded. */
  fetch: FetchHandler<K, V, H, C>;
  /** A method that will be called when data needs to be collected from the entry table for a
   * given request. Must collect the data from the entry table and return the correct results
   * for the given key. */
  collect: CollectHandler<K, V, H, C>;
  /** A method that accepts the key and data of any entry that was just published, and calls
   * methods to indicate which request keys are affected by the change. */
  affectedKeys: AffectedKeysHandler<K, V, H, C>;
  /** A method called for each key subscribed to, the first time it's subscribed to, or the
   * first time it's subscribed to, after having been fully unsubscribed from. */
  initialSubscribe?: FirstLastSubscribeHandler<K, V, H, C>;
  /** A method called when the last subscriber for a given key unsubscribes. May be called again
   * for a key if, for instance, a new subscription is started for it and then unsubscribes. */
  finalUnsubscribe?: FirstLastSubscribeHandler<K, V, H, C>;
  /** A method called immediately before data is submitted to a currently active
   * subscription. */
  subscriptionPublished?: SubscriptionPublishedHandler<K, V, H, C>;
  /** The time, in seconds, after which a request will expire. If the data is requested again,
   * it will refetch.
   *
   * Defaults to 300 (5 minutes). */
  expiresAfterSeconds?: number | false;
  /** The time, in seconds, after which expiry cache will be purged. Set to null to disable
   * purging. Defaults to 300 (5 minutes). */
  purgeExpirationAfterSeconds?: number | null;
  /** A key hasher for hashing request keys into strings. */
  requestKeyHasher?: H;
  /** A clock to handle expiration timing of requests. */
  clock?: C;
}

/** Indicates that the data is expired or was not fetched yet. */
export const Expired = Symbol("Expired");

/** A caching service for client-side repositories.
 *
 * Requires implementing three callbacks:
 * - Provide a `fetch` method for fetching repository data from the server and distributing the
 *   pieces into an id-keyed table.
 * - Provide a `collect` method for collecting the data out of the id-keyed table to provide to
 *   requests for the data.
 * - Provide an `affectedKeys` method for computing which requests are affected when an entry in
 *   the table is updated.
 *
 * The four type parameters are as follows:
 * - `K`: The type for unique keys used to map to individual entities.
 * - `V`: The type for the entities stored by this cache.
 * - `H`: A key hasher used for hashing details for a fetch request.
 * - `C`: A clock to use for cache expiration timing. */
export class RepoCache<
  K,
  V,
  H extends KeyHasher<Key<H>, Pattern<H>>,
  C extends Clock<Time<C>, Duration<C>>,
> {
  #hasher: H;
  #expiration: ExpirationCache<StringKeyHasher, C>;
  #observables: ObservableCache<StringKeyHasher>;
  #fetchPromises = new Map<string, Promise<void>>();
  #fetch: FetchHandler<K, V, H, C>;
  #collect: CollectHandler<K, V, H, C>;
  #affectedKeys: AffectedKeysHandler<K, V, H, C>;
  #subscriptionPublished?: SubscriptionPublishedHandler<K, V, H, C>;
  #affectedKeySet = new Set<string>();
  #affectedPatternSet = new Set<string>();
  #deferredPublish: Promise<void> | null = null;

  /** The data-table. Maps data entry keys to the individual data entries. */
  table = new Map<K, V>();

  constructor({
    fetch,
    collect,
    affectedKeys,
    initialSubscribe,
    finalUnsubscribe,
    subscriptionPublished,
    expiresAfterSeconds = 60 * 5,
    purgeExpirationAfterSeconds,
    requestKeyHasher = new ArrayKeyHasher() as H,
    clock = new PerformanceClock() as C,
  }: RepoCacheOptions<K, V, H, C>) {
    this.#hasher = requestKeyHasher;
    this.#fetch = fetch;
    this.#collect = collect;
    this.#affectedKeys = affectedKeys;
    this.#subscriptionPublished = subscriptionPublished;

    this.#expiration = new ExpirationCache({
      keyHasher: new StringKeyHasher(),
      clock,
      defaultExpirationTime: expiresAfterSeconds,
      purgeAfter: purgeExpirationAfterSeconds,
    });
    this.#observables = new ObservableCache({
      keyHasher: new StringKeyHasher(),
      onInitialSubscribe: initialSubscribe
        ? (strKey) => {
            const requestKey = this.#hasher.unhash(strKey);
            initialSubscribe({ repoCache: this, requestKey, strKey });
          }
        : undefined,
      onFinalUnsubscribe: finalUnsubscribe
        ? (strKey) => {
            const requestKey = this.#hasher.unhash(strKey);
            finalUnsubscribe({ repoCache: this, requestKey, strKey });
          }
        : undefined,
    });
  }

  /** Trigger the registered `affectedKeys` handler with the appropriate callbacks. Stores the
   * set of affected keys / patterns in the given sets. */
  #affectKeysFor(
    affectedKeySet: Set<string>,
    affectedPatternSet: Set<string>,
    entryKey: K,
    entryData: V,
    deleted: boolean
  ) {
    // prepare 'affected' methods
    const affectsKey = (key: Key<H>) => {
      affectedKeySet.add(this.#hasher.hash(key));
    };
    const affectsMatchingKeys = (pattern: Pattern<H>) => {
      affectedPatternSet.add(this.#hasher.hashPattern(pattern));
    };

    // call affectedKeys callback
    this.#affectedKeys({
      repoCache: this,
      entryKey,
      entryData,
      deleted,
      affectsKey,
      affectsMatchingKeys,
    });
  }

  /** Pull and clear the affected key and pattern sets, and filter the passed array of request
   * keys, returning the ones that are affected by the sets. */
  #getAffectedKeys(
    affectedKeySet: Set<string>,
    affectedPatternSet: Set<string>,
    actualKeys: string[]
  ): [string, Key<H>][] {
    // copy out affected keys / patterns
    const keySet = new Set(affectedKeySet);
    const patterns = [...affectedPatternSet].map((pattern) =>
      this.#hasher.unhashPattern(pattern)
    );

    // clear affected keys / patterns
    affectedKeySet.clear();
    affectedPatternSet.clear();

    const affected: [string, Key<H>][] = [];

    // iterate all keys, and find and re-publish data for affected keys
    for (const strKey of actualKeys) {
      const key = this.#hasher.unhash(strKey);

      // try to determine if key doesn't match:

      // if key wasn't in plain affected key set..
      if (!keySet.has(strKey)) {
        let matches = false;

        for (const pattern of patterns) {
          if (this.#hasher.matches(key, pattern)) {
            matches = true;
            break;
          }
        }

        // ..and key also didn't match any patterns..
        if (!matches) continue; // ..then key wasn't affected
      }

      // otherwise, it was affected
      affected.push([strKey, key]);
    }
    return affected;
  }

  /** After a fetch has finished, or after mutation operations receive updates, this method will
   * be called in the next async frame to dispatch all updates to affected keys. */
  #publishDeferred() {
    // reset defer call promise
    this.#deferredPublish = null;

    // find affected keys
    const affected = this.#getAffectedKeys(
      this.#affectedKeySet,
      this.#affectedPatternSet,
      this.#observables.getAllKeys()
    );

    // re-publish data for affected keys
    for (const [strKey, key] of affected) {
      if (this.#observables.has(strKey)) {
        const data = this.collect(key);
        this.#subscriptionPublished?.({
          repoCache: this,
          requestKey: key,
          strKey,
          collectedData: data,
        });
        this.#observables.publish(strKey, data);
      }
    }
  }

  /** This method will defer the call to publishDeferred. */
  #deferPublish() {
    // trigger deferred publish if it hasn't been deferred yet
    if (!this.#deferredPublish) {
      this.#deferredPublish = Promise.resolve().then(() =>
        this.#publishDeferred()
      );
    }
  }

  /** Determines if the request by the given key was never fetched or has expired. */
  isExpired(requestKey: Key<H>) {
    const strKey = this.#hasher.hash(requestKey);
    return this.#expiration.isExpired(strKey);
  }

  /** Determines if the request by the given key is currently in use by one or more
   * observables. */
  isUsed(requestKey: Key<H>) {
    const strKey = this.#hasher.hash(requestKey);
    return this.#observables.has(strKey);
  }

  /** Get all keys for request subscribers. */
  getAllKeys(): Key<H>[] {
    const keys: Key<H>[] = [];
    for (const strKey of this.#observables.getAllKeys()) {
      keys.push(this.#hasher.unhash(strKey));
    }
    return keys;
  }

  /** Get any existing subscriber keys that match the given pattern. */
  getMatchingKeys(pattern: Pattern<H>): Key<H>[] {
    const keys: Key<H>[] = [];
    for (const strKey of this.#observables.getAllKeys()) {
      const key = this.#hasher.unhash(strKey);
      if (this.#hasher.matches(key, pattern)) {
        keys.push(key);
      }
    }
    return keys;
  }

  /** Returns true if the given request key is in the process of fetching. */
  isFetching(requestKey: Key<H>): boolean {
    const strKey = this.#hasher.hash(requestKey);
    return this.#fetchPromises.has(strKey);
  }

  /** Manually trigger a refetch for the given request key. Subscribed observables will
   * automatically re-trigger.
   *
   * **This method does not return the server response data.** */
  async fetch(requestKey: Key<H>): Promise<void> {
    const strKey = this.#hasher.hash(requestKey);

    // check if a fetch for this key is already in progress
    let promise: void | Promise<void> = this.#fetchPromises.get(strKey);
    if (promise) return promise; // await promise, and bail

    // get the fetch promise
    promise = this.#fetch({
      repoCache: this,
      publish: this.publish.bind(this),
      requestKey,
    });

    // if it's a promise, cache it to prevent simultaneous calls
    if (promise instanceof Promise) {
      this.#fetchPromises.set(strKey, promise);

      // wait for completion
      await promise.finally(() => {
        // after completion, remove promise from cache to allow for another call
        // (unless it was somehow replaced)
        const prom = this.#fetchPromises.get(strKey);
        if (prom === promise) this.#fetchPromises.delete(strKey);
      });
    }

    // register it as cached
    this.#expiration.set(strKey);

    // always affect the key requested
    this.#affectedKeySet.add(strKey);
    this.#deferPublish();
  }

  /** Collect the data for the given key from the locally cached values. This synchronously
   * returns the available cached data, which may be empty or null if no fetches have run yet.
   *
   * This method will not trigger any refetching. Use `fetch` to trigger refetching. Once it
   * resolves, this method can be called with the same key to retrieve the fetched data. */
  collect<T>(requestKey: Key<H>): T {
    return this.#collect({
      repoCache: this,
      requestKey,
      table: this.table,
    }) as T;
  }

  /** Publishes the updated individual entry to the data table. */
  publish(entryKey: K, entryData: V | null) {
    let deleted = false;

    // update the entry in the data table
    if (entryData === null) {
      deleted = true;
      entryData = this.table.get(entryKey) ?? null;
      this.table.delete(entryKey); // delete if null
    } else {
      this.table.set(entryKey, entryData); // update otherwise
    }

    // If entryData is null, it's because the entry didn't previously exist *and yet* the
    // publish call is deleting it, in which case nothing needs to happen.
    if (!entryData) return;

    // get the affected keys for this entry
    this.#affectKeysFor(
      this.#affectedKeySet,
      this.#affectedPatternSet,
      entryKey,
      entryData,
      deleted
    );

    this.#deferPublish();
  }

  /** Get an observable for the given key. Upon subscription, the data for the key will be
   * provided synchronously if it has not expired, or will otherwise be refetched from the
   * server and provided asynchronously as the first value.
   *
   * If `forceReload` is true, the data will never be provided synchronously, but will be forced
   * to reload from the server, regardless of the cache state. */
  observable<T>(
    requestKey: Key<H>,
    { forceReload = false, initialFetch = true }: GetObservableOptions = {}
  ): Observable<T> {
    const strKey = this.#hasher.hash(requestKey);

    return this.#observables.observable(strKey, (subscriber) => {
      if (!initialFetch) return;

      if (forceReload || this.#expiration.isExpired(strKey)) {
        this.fetch(requestKey);
      } else {
        subscriber.next(this.collect(requestKey));
      }
    });
  }

  /** Collect the data for the given request key, if it is present and not expired. Otherwise,
   * a fallback is returned, which defaults to the unique `Expired` symbol. */
  collectIfUnexpired<T, F = typeof Expired>(
    requestKey: Key<H>,
    fallback: F = Expired as F
  ): T | NoInfer<F> {
    if (this.isExpired(requestKey)) return fallback;
    return this.collect(requestKey);
  }

  /** Clear all cache, requiring any following fetch to re-run. */
  clear() {
    this.table.clear();
    this.#expiration.clear();
  }

  /** Removes a single entry from the cache, and invalidates any request keys affected by it. */
  invalidateEntry(key: K) {
    const entryData = this.table.get(key);
    if (!entryData) return;

    // remove entry
    this.table.delete(key);

    // get affected request keys / patterns
    const affectedKeySet = new Set<string>();
    const affectedPatternSet = new Set<string>();
    this.#affectKeysFor(
      affectedKeySet,
      affectedPatternSet,
      key,
      entryData,
      false
    );

    // get and invalidate matched request keys
    const keys = this.#getAffectedKeys(
      affectedKeySet,
      affectedPatternSet,
      this.#expiration.getAllKeys()
    );
    for (const [key] of keys) {
      this.#expiration.delete(key, false);
    }
  }

  /** Clear some of the cache, filtered by some predicate. If the predicate returns true, the
   * entry will be removed. */
  invalidateMatching(predicate: (entry: V, key: K) => boolean) {
    // prepare affected key sets
    const affectedKeySet = new Set<string>();
    const affectedPatternSet = new Set<string>();

    // iterate all entries, remove matching, and track affected request keys / patterns
    for (const [key, value] of this.table) {
      if (predicate(value, key)) {
        this.table.delete(key);
        this.#affectKeysFor(
          affectedKeySet,
          affectedPatternSet,
          key,
          value,
          false
        );
      }
    }

    // get and invalidate matched request keys
    const keys = this.#getAffectedKeys(
      affectedKeySet,
      affectedPatternSet,
      this.#expiration.getAllKeys()
    );
    for (const [key] of keys) {
      this.#expiration.delete(key, false);
    }
  }
}
